// SettingsPage.jsx
import React, { useState, useEffect } from 'react';
import {
    Typography,
    TextField,
    Button,
    Box,
    Card,
    CardContent,
    Chip,
    Avatar,
    Fade,
    Slide,
    useTheme,
    alpha
} from '@mui/material';
import {
    Save,
    Cancel as CancelIcon,
    Edit,
    PhoneAndroid,
    Percent,
    ToggleOn
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import SettingsService from '../../services/SettingsService';
import AuthService from '../../services/AuthService';
import HeaderTopSection from "../../components/partials/HeaderTop";
import Banner from "../../components/partials/Banner";
import LoadingSpinner from "../../utils/LoadingSpinner";

const emptyFormData = {
    orangeMoneyNumber: "",
    orangeMoneyHolderName: "",
    mtnMomoNumber: "",
    mtnMomoHolderName: "",
    commission: "",
    status: "ACTIVE"
};

const SettingsPage = () => {
    const [isEditMode, setIsEditMode] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [settingsId, setSettingsId] = useState(null);
    const [formData, setFormData] = useState(emptyFormData);

    const theme = useTheme();

    // Couleurs par défaut sécurisées
    const getThemeColor = (colorType = 'primary') => {
        const colors = {
            primary: theme.palette?.primary?.main || '#1976d2',
            secondary: theme.palette?.secondary?.main || '#dc004e',
            success: theme.palette?.success?.main || '#2e7d32',
            info: theme.palette?.info?.main || '#0288d1',
            warning: theme.palette?.warning?.main || '#ed6c02',
            error: theme.palette?.error?.main || '#d32f2f'
        };
        return colors[colorType] || colors.primary;
    };

    const getAlphaColor = (colorType = 'primary', opacity = 0.1) => {
        const color = getThemeColor(colorType);
        return alpha(color, opacity);
    };

    // Mappe la réponse API vers le state du formulaire
    const mapSettingsToFormData = (settingsData) => ({
        orangeMoneyNumber: settingsData.orangeMoneyNumber || "",
        orangeMoneyHolderName: settingsData.orangeMoneyHolderName || "",
        mtnMomoNumber: settingsData.mtnMomoNumber || "",
        mtnMomoHolderName: settingsData.mtnMomoHolderName || "",
        commission: settingsData.commission ?? "",
        status: settingsData.status || "ACTIVE"
    });

    const fetchCurrentSettings = async () => {
        try {
            const settingsData = await SettingsService.getCurrentSettings();
            setSettingsId(settingsData.id);
            setFormData(mapSettingsToFormData(settingsData));
        } catch (error) {
            console.error('Erreur chargement des paramètres:', error);
            toast.error("Impossible de charger les paramètres");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCurrentSettings();
    }, []);

    const handleFieldChange = (field) => (event) => {
        setFormData(prev => ({
            ...prev,
            [field]: event.target.value
        }));
    };

    const handleSave = async () => {
        if (!settingsId) {
            toast.error("Aucun paramètre à mettre à jour");
            return;
        }

        try {
            setSaving(true);
            const user = AuthService.getCurrentUser();

            const payload = {
                orangeMoneyNumber: formData.orangeMoneyNumber,
                orangeMoneyHolderName: formData.orangeMoneyHolderName,
                mtnMomoNumber: formData.mtnMomoNumber,
                mtnMomoHolderName: formData.mtnMomoHolderName,
                commission: formData.commission !== "" ? parseFloat(formData.commission) : null,
                // Le statut n'est pas modifiable depuis ce formulaire, on renvoie la valeur existante
                status: formData.status,
                lastModifiedBy: user?.username || user?.email || "admin"
            };

            const updated = await SettingsService.updateSettings(settingsId, payload);
            setFormData(mapSettingsToFormData(updated));
            toast.success('Paramètres mis à jour avec succès');
            setIsEditMode(false);
        } catch (error) {
            console.error('Erreur mise à jour des paramètres:', error);
            toast.error("Erreur lors de la mise à jour des paramètres");
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = async () => {
        setIsEditMode(false);
        setLoading(true);
        await fetchCurrentSettings();
    };

    // Composant pour afficher une information en mode lecture
    const InfoCard = ({ icon, title, value, color = 'primary' }) => {
        const bgColor = getAlphaColor(color, 0.05);
        const borderColor = getAlphaColor(color, 0.1);
        const avatarColor = getThemeColor(color);

        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 2,
                    p: 2.5,
                    borderRadius: 2,
                    backgroundColor: bgColor,
                    border: `1px solid ${borderColor}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                        backgroundColor: getAlphaColor(color, 0.08),
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }
                }}
            >
                <Avatar
                    sx={{
                        backgroundColor: getAlphaColor(color, 0.1),
                        color: avatarColor,
                        width: 48,
                        height: 48
                    }}
                >
                    {icon}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                    <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5 }}
                    >
                        {title}
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.primary', lineHeight: 1.5 }}>
                        {value}
                    </Typography>
                </Box>
            </Box>
        );
    };

    return (
        <>
            {/* Spinner de chargement personnalisé */}
            <LoadingSpinner
                isVisible={loading}
                loadingText="Chargement des paramètres..."
            />

            <HeaderTopSection />
            <Banner title="Paramètres Système" />

            {!loading && (
                <Fade in={!loading} timeout={500}>
                    <Card
                        sx={{
                            maxWidth: 800,
                            mx: 'auto',
                            mt: 2,
                            background: `linear-gradient(135deg, ${getAlphaColor('primary', 0.02)} 0%, ${getAlphaColor('secondary', 0.02)} 100%)`,
                            border: `1px solid ${getAlphaColor('primary', 0.1)}`,
                            boxShadow: '0 8px 32px rgba(0,0,0,0.04)',
                            borderRadius: 3,
                            overflow: 'hidden'
                        }}
                    >
                        {/* En-tête avec dégradé */}
                        <Box
                            sx={{
                                background: `linear-gradient(135deg, ${getThemeColor('primary')} 0%, ${getThemeColor('primary')}99 100%)`,
                                color: 'white',
                                p: 3
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box>
                                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                                        Paramètres de Paiement & Commission
                                    </Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 300, color: 'white' }}>
                                        Gérez les numéros mobile money et le taux de commission de la plateforme
                                    </Typography>
                                </Box>

                                {!isEditMode ? (
                                    <Button
                                        startIcon={<Edit />}
                                        onClick={() => setIsEditMode(true)}
                                        variant="contained"
                                        sx={{
                                            backgroundColor: 'white',
                                            color: getThemeColor('primary'),
                                            fontWeight: 600,
                                            '&:hover': {
                                                backgroundColor: alpha('#fff', 0.9),
                                                transform: 'translateY(-1px)'
                                            },
                                            transition: 'all 0.2s ease'
                                        }}
                                    >
                                        Modifier
                                    </Button>
                                ) : (
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button
                                            startIcon={<CancelIcon />}
                                            onClick={handleCancel}
                                            disabled={saving}
                                            variant="outlined"
                                            sx={{
                                                borderColor: 'white',
                                                color: 'white',
                                                '&:hover': {
                                                    backgroundColor: alpha('#fff', 0.1),
                                                    borderColor: 'white'
                                                }
                                            }}
                                        >
                                            Annuler
                                        </Button>
                                        <Button
                                            startIcon={saving ? null : <Save />}
                                            variant="contained"
                                            onClick={handleSave}
                                            disabled={saving}
                                            sx={{
                                                backgroundColor: getThemeColor('success'),
                                                '&:hover': {
                                                    backgroundColor: alpha(getThemeColor('success'), 0.8),
                                                    transform: 'translateY(-1px)'
                                                },
                                                transition: 'all 0.2s ease'
                                            }}
                                        >
                                            {saving ? 'Enregistrement...' : 'Sauvegarder'}
                                        </Button>
                                    </Box>
                                )}
                            </Box>
                        </Box>

                        <CardContent sx={{ p: 4 }}>
                            <Slide in={true} direction="up" timeout={800}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

                                    {/* Orange Money */}
                                    {isEditMode ? (
                                        <Box
                                            sx={{
                                                p: 3,
                                                borderRadius: 2,
                                                backgroundColor: getAlphaColor('warning', 0.03),
                                                border: `1px dashed ${getAlphaColor('warning', 0.2)}`
                                            }}
                                        >
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                                                🟠 Orange Money
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                                <TextField
                                                    label="Numéro Orange Money"
                                                    value={formData.orangeMoneyNumber}
                                                    onChange={handleFieldChange('orangeMoneyNumber')}
                                                    variant="outlined"
                                                    sx={{ flex: 1, minWidth: 220 }}
                                                />
                                                <TextField
                                                    label="Nom du titulaire"
                                                    value={formData.orangeMoneyHolderName}
                                                    onChange={handleFieldChange('orangeMoneyHolderName')}
                                                    variant="outlined"
                                                    sx={{ flex: 1, minWidth: 220 }}
                                                />
                                            </Box>
                                        </Box>
                                    ) : (
                                        <InfoCard
                                            icon={<PhoneAndroid />}
                                            title="Orange Money"
                                            value={
                                                formData.orangeMoneyNumber ? (
                                                    <Box>
                                                        <Typography variant="body1">{formData.orangeMoneyNumber}</Typography>
                                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                            Titulaire : {formData.orangeMoneyHolderName || "Non spécifié"}
                                                        </Typography>
                                                    </Box>
                                                ) : "Non configuré"
                                            }
                                            color="warning"
                                        />
                                    )}

                                    {/* MTN Momo */}
                                    {isEditMode ? (
                                        <Box
                                            sx={{
                                                p: 3,
                                                borderRadius: 2,
                                                backgroundColor: getAlphaColor('info', 0.03),
                                                border: `1px dashed ${getAlphaColor('info', 0.2)}`
                                            }}
                                        >
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                                                🟡 MTN Mobile Money
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                                <TextField
                                                    label="Numéro MTN Momo"
                                                    value={formData.mtnMomoNumber}
                                                    onChange={handleFieldChange('mtnMomoNumber')}
                                                    variant="outlined"
                                                    sx={{ flex: 1, minWidth: 220 }}
                                                />
                                                <TextField
                                                    label="Nom du titulaire"
                                                    value={formData.mtnMomoHolderName}
                                                    onChange={handleFieldChange('mtnMomoHolderName')}
                                                    variant="outlined"
                                                    sx={{ flex: 1, minWidth: 220 }}
                                                />
                                            </Box>
                                        </Box>
                                    ) : (
                                        <InfoCard
                                            icon={<PhoneAndroid />}
                                            title="MTN Mobile Money"
                                            value={
                                                formData.mtnMomoNumber ? (
                                                    <Box>
                                                        <Typography variant="body1">{formData.mtnMomoNumber}</Typography>
                                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                            Titulaire : {formData.mtnMomoHolderName || "Non spécifié"}
                                                        </Typography>
                                                    </Box>
                                                ) : "Non configuré"
                                            }
                                            color="info"
                                        />
                                    )}

                                    {/* Commission */}
                                    {isEditMode ? (
                                        <Box
                                            sx={{
                                                p: 3,
                                                borderRadius: 2,
                                                backgroundColor: getAlphaColor('success', 0.03),
                                                border: `1px dashed ${getAlphaColor('success', 0.2)}`
                                            }}
                                        >
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                                                Taux de Commission
                                            </Typography>
                                            <TextField
                                                type="number"
                                                value={formData.commission}
                                                onChange={handleFieldChange('commission')}
                                                label="Commission (%)"
                                                variant="outlined"
                                                sx={{ width: 220 }}
                                                InputProps={{ inputProps: { min: 0, max: 100, step: 0.5 } }}
                                                helperText="Pourcentage prélevé sur les frais de service du client"
                                            />
                                        </Box>
                                    ) : (
                                        <InfoCard
                                            icon={<Percent />}
                                            title="Taux de Commission"
                                            value={
                                                formData.commission !== "" && formData.commission !== null
                                                    ? `${formData.commission} %`
                                                    : "Non configuré"
                                            }
                                            color="success"
                                        />
                                    )}

                                    {/* Statut — lecture seule uniquement, non modifiable depuis ce formulaire */}
                                    {!isEditMode && (
                                        <InfoCard
                                            icon={<ToggleOn />}
                                            title="Statut"
                                            value={
                                                <Chip
                                                    label={formData.status === "ACTIVE" ? "Actif" : "Inactif"}
                                                    color={formData.status === "ACTIVE" ? "success" : "default"}
                                                    variant="filled"
                                                    size="small"
                                                />
                                            }
                                            color={formData.status === "ACTIVE" ? "success" : "default"}
                                        />
                                    )}
                                </Box>
                            </Slide>

                            {isEditMode && (
                                <Box
                                    sx={{
                                        mt: 3,
                                        p: 2,
                                        borderRadius: 2,
                                        backgroundColor: getAlphaColor('info', 0.1),
                                        border: `1px solid ${getAlphaColor('info', 0.2)}`,
                                        textAlign: 'center'
                                    }}
                                >
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        💡 Mode édition activé - Modifiez les paramètres puis sauvegardez
                                    </Typography>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Fade>
            )}
        </>
    );
};

export default SettingsPage;